import { AnNamElement } from "../../base-element";

type DawnCart = HTMLElement & {
  getSectionsToRender: () => Array<{ id: string }>;
  renderContents: (data: unknown) => void;
  setActiveElement?: (el: Element) => void;
};

export class AnQuickAdd extends AnNamElement {
  connectedCallback(): void {
    super.connectedCallback();
    // Intercept clicks on [data-an-quick-add] buttons.
    // preventDefault() on click stops the browser's default click action,
    // which for <button type="submit"> inside <form> prevents the form 'submit'
    // event — so Dawn's product-form.js (which calls preventDefault on submit)
    // never runs. We handle the full cart add + drawer refresh ourselves.
    document.addEventListener("click", this.onQuickAddClick);
  }

  disconnectedCallback(): void {
    document.removeEventListener("click", this.onQuickAddClick);
    super.disconnectedCallback();
  }

  private setButtonLabel(button: HTMLButtonElement, text: string): void {
    const span = button.querySelector<HTMLElement>("span:not(.sold-out-message):not(.loading__spinner)");
    if (span) span.textContent = text;
    else button.firstChild && (button.firstChild.textContent = text);
  }

  private onQuickAddClick = async (event: Event): Promise<void> => {
    const target = event.target as HTMLElement | null;
    const button = target?.closest("[data-an-quick-add]") as HTMLButtonElement | null;
    if (!button) return;

    const variantId = Number(button.dataset.variantId);
    if (!variantId) return;

    // preventDefault() on click blocks the browser's default action.
    // For a <button type="submit"> inside a <form>, this prevents the
    // form 'submit' event — so Dawn's product-form.js never fires.
    event.preventDefault();
    button.disabled = true;
    button.querySelector(".loading__spinner")?.classList.remove("hidden");
    const defaultLabel = button.dataset.defaultLabel ?? "Add to cart";

    const cart = (
      document.querySelector("cart-notification") ?? document.querySelector("cart-drawer")
    ) as DawnCart | null;

    const formData = new FormData();
    formData.append("id", String(variantId));
    formData.append("quantity", "1");
    if (cart) {
      formData.append("sections", cart.getSectionsToRender().map((s) => s.id).join(","));
      formData.append("sections_url", window.location.pathname);
      cart.setActiveElement?.(button);
    }

    try {
      const response = await fetch("/cart/add", {
        method: "POST",
        credentials: "same-origin",
        headers: { "X-Requested-With": "XMLHttpRequest", Accept: "application/json" },
        body: formData,
      });
      const data = (await response.json()) as Record<string, unknown>;

      if (!response.ok || data["status"]) {
        throw new Error((data["description"] as string | undefined) ?? "Unable to add item to cart.");
      }

      // Refresh the cart drawer with the server response
      cart?.renderContents(data);
      document.dispatchEvent(new CustomEvent("an:cart-updated", { bubbles: true }));

      this.setButtonLabel(button, "Added");
      window.setTimeout(() => this.setButtonLabel(button, defaultLabel), 1500);
    } catch {
      this.setButtonLabel(button, "Try again");
      window.setTimeout(() => this.setButtonLabel(button, defaultLabel), 2000);
    } finally {
      button.disabled = false;
      button.querySelector(".loading__spinner")?.classList.add("hidden");
    }
  };
}

if (!customElements.get("an-quick-add")) {
  customElements.define("an-quick-add", AnQuickAdd);
}
